// components/DocumentGeneratorModal.tsx
import React, { useEffect, useState } from 'react';
import { generateDocumentFromTemplate, type DocumentTemplate } from '../api/employees';
import { triggerFileDownload } from '../api/documents';
import { createDomiciliation } from '../api/domiciliation';
import { fetchTemplateFields, type SaisieField } from '../api/templateBuilder';
import DocumentViewer from './DocumentViewer';

interface DocumentGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: DocumentTemplate;
  employeeId: number;
  employeeName: string;
}

const DocumentGeneratorModal: React.FC<DocumentGeneratorModalProps> = ({
  isOpen,
  onClose,
  template,
  employeeId,
  employeeName,
}) => {
  const [generating, setGenerating] = useState(false);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  // États pour la prévisualisation
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string>('');

  // ─── Détection des modèles à champs supplémentaires ──────────────────────
  //
  // Ces six drapeaux, et les six blocs de saisie qu'ils commandent (~200 lignes
  // plus bas), sont un codage en dur : chaque modèle « spécial » est reconnu à
  // une sous-chaîne de son nom de fichier. C'est fragile — un modèle nommé
  // « Attestation de SOLDE annuel » déclencherait le bloc « solde de tout
  // compte » — et tout nouveau modèle exige de modifier ce composant.
  //
  // Le remplacement existe déjà et fonctionne : les marqueurs « {{ saisie: … }} »
  // (voir `saisieFields` juste en dessous, et
  // backend/documents/tests_template_builder.py). Un modèle qui les porte
  // apporte ses propres champs, sans toucher à ce fichier.
  //
  // Migration, modèle par modèle :
  //   1. dans le .docx, remplacer le champ par « {{ saisie: Libellé du champ }} » ;
  //   2. vérifier que le champ apparaît bien dans la fenêtre de génération ;
  //   3. supprimer ici le drapeau `isXxxTemplate`, ses `useState` et son bloc
  //      de saisie, ainsi que les entrées correspondantes de la charge utile.
  // Aucun des deux mécanismes ne gêne l'autre : ils peuvent coexister pendant
  // toute la migration.
  const isMaternityTemplate = (template.display_name || template.filename || '').toUpperCase().includes('MATERNITE');
  const isDomiciliationTemplate = (template.display_name || template.filename || '').toUpperCase().includes('DOMICILIATION');
  const isRepriseServiceTemplate = (template.display_name || template.filename || '').toUpperCase().includes('REPRISE');
  const isSoldeToutCompteTemplate = (template.display_name || template.filename || '').toUpperCase().includes('SOLDE');
  const isNotificationFinContratTemplate = (template.display_name || template.filename || '').toUpperCase().includes('NOTIFICATION');
  const isProrogationCDDTemplate = (template.display_name || template.filename || '').toUpperCase().includes('PROROGATION');

  // États pour congé de maternité
  const [dateStartConges, setDateStartConges] = useState('');
  const [dateEndConges, setDateEndConges] = useState('');
  const [dateReprise, setDateReprise] = useState('');
  
  // États pour domiciliation de salaire
  const [nameBanque, setNameBanque] = useState('');
  const [numeroCompte, setNumeroCompte] = useState('');
  const [salary, setSalary] = useState('');
  
  // États pour reprise de service
  const [dateDepartConges, setDateDepartConges] = useState('');
  
  // États pour solde de tout compte
  const [montantSolde, setMontantSolde] = useState('');
  const [montantIndemniteFinContrat, setMontantIndemniteFinContrat] = useState('');
  const [montantIndemniteCongesPayes, setMontantIndemniteCongesPayes] = useState('');
  const [montantIndemniteGratif, setMontantIndemniteGratif] = useState('');
  const [montantRetenues, setMontantRetenues] = useState('');
  
  // États pour notification de fin de contrat
  const [reference, setReference] = useState('');

  // États pour prorogation CDD
  const [numAvenant, setNumAvenant] = useState('');
  const [debutDate, setDebutDate] = useState('');
  const [finDate, setFinDate] = useState('');

  // Champs déclarés par le modèle lui-même (marqueurs « {{ saisie: … }} »).
  // Ils remplacent le codage en dur : un nouveau modèle apporte ses propres
  // champs sans qu'on touche à ce composant.
  const [saisieFields, setSaisieFields] = useState<SaisieField[]>([]);
  const [saisieValues, setSaisieValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    const chemin = template.relative_path || `templates/${template.filename}`;
    let actif = true;
    fetchTemplateFields(chemin).then(champs => {
      if (!actif) return;
      setSaisieFields(champs);
      setSaisieValues(Object.fromEntries(champs.map(c => [c.key, ''])));
    });
    return () => { actif = false; };
  }, [isOpen, template.relative_path, template.filename]);

  // Date d'établissement (commune à tous les templates)
  const [dateEtablissement, setDateEtablissement] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  if (!isOpen) return null;

  const handleGenerate = async (autoDownload: boolean = false) => {
    try {
      setGenerating(true);
      setError(null);

      // Fonction pour convertir YYYY-MM-DD en JJ/MM/AAAA
      const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };

      // Préparer les champs supplémentaires selon le template
      let extras: Record<string, string> | undefined = undefined;

      if (isMaternityTemplate) {
        // Validation
        if (!dateStartConges.trim()) {
          setError('La date de début de congés est requise.');
          setGenerating(false);
          return;
        }
        
        extras = {
          date_startconges: formatDate(dateStartConges),
          date_endconges: formatDate(dateEndConges),
          date_reprise: formatDate(dateReprise),
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isDomiciliationTemplate) {
        // Validation
        if (!nameBanque.trim() || !numeroCompte.trim() || !salary.trim()) {
          setError('Le nom de la banque, le numéro de compte et le salaire sont requis.');
          setGenerating(false);
          return;
        }
        
        extras = {
          name_banque: nameBanque,
          numero_compte: numeroCompte,
          salary: salary,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isRepriseServiceTemplate) {
        // Validation
        if (!dateDepartConges.trim()) {
          setError('La date de départ en congés est requise.');
          setGenerating(false);
          return;
        }
        
        extras = {
          date_depart_conges: formatDate(dateDepartConges),
          date_reprise: formatDate(dateReprise),
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isSoldeToutCompteTemplate) {
        // Validation
        if (!montantSolde.trim()) {
          setError('Le montant du solde est requis.');
          setGenerating(false);
          return;
        }
        
        extras = {
          montant_solde: montantSolde,
          montant_indemnit_fin_contrat: montantIndemniteFinContrat,
          montant_indemnit_conges_payes: montantIndemniteCongesPayes,
          montant_indemnit_gratif: montantIndemniteGratif,
          montant_retenues: montantRetenues,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isNotificationFinContratTemplate) {
        // Validation
        if (!reference.trim()) {
          setError('La référence du courrier est requise.');
          setGenerating(false);
          return;
        }

        extras = {
          reference: reference,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isProrogationCDDTemplate) {
        // Validation
        if (!numAvenant.trim()) {
          setError('Le numéro d\'avenant est requis.');
          setGenerating(false);
          return;
        }
        if (!debutDate.trim()) {
          setError('La date de début est requise.');
          setGenerating(false);
          return;
        }
        if (!finDate.trim()) {
          setError('La date de fin de contrat est requise.');
          setGenerating(false);
          return;
        }

        extras = {
          num_avenant: numAvenant,
          debut_date: formatDate(debutDate),
          fin_date: formatDate(finDate),
          date_etablissement: formatDate(dateEtablissement),
        };
      }

      // Les champs déclarés par le modèle s'ajoutent aux extras : la clé est
      // l'expression exacte du marqueur, que le moteur retrouve dans le contexte.
      // Les champs de type date arrivent en AAAA-MM-JJ depuis <input type="date"> :
      // on les remet au format français avant de les insérer dans le document.
      const typeParCle = Object.fromEntries(saisieFields.map(c => [c.key, c.type]));
      const renseignes = Object.entries(saisieValues)
        .filter(([, v]) => v.trim())
        .map(([cle, valeur]) => [
          cle,
          typeParCle[cle] === 'date' ? formatDate(valeur) : valeur,
        ]);
      if (renseignes.length > 0) {
        extras = { ...(extras || {}), ...Object.fromEntries(renseignes) };
      }

      // relative_path conserve le sous-dossier de catégorie ; filename seul le
      // perdrait et le modèle serait introuvable.
      const cheminModele = template.relative_path || template.filename;
      const blob = await generateDocumentFromTemplate(employeeId, cheminModele, { extras });
      const url = URL.createObjectURL(blob);
      setDocUrl(url);
      
      // Enregistrer la domiciliation côté serveur : elle devient visible par
      // toute l'équipe, avec le nom de son auteur. Si le réseau échoue, on
      // retombe sur le localStorage — la reprise automatique de la page
      // Domiciliation la remontera au prochain passage, rien n'est perdu.
      if (isDomiciliationTemplate && extras) {
        try {
          await createDomiciliation({
            employee: employeeId,
            bank_name: nameBanque,
            account_number: numeroCompte,
            salary: salary,
            establishment_date: dateEtablissement,
          });
        } catch (saveErr) {
          console.warn('Domiciliation non enregistrée en ligne, sauvegarde locale de secours', saveErr);
          try {
            const existingData = localStorage.getItem('domiciliations');
            const domiciliations = existingData ? JSON.parse(existingData) : [];
            domiciliations.push({
              id: Date.now(),
              employee_id: employeeId,
              employee_name: employeeName,
              bank_name: nameBanque,
              account_number: numeroCompte,
              salary: salary,
              establishment_date: dateEtablissement,
              created_at: new Date().toISOString(),
            });
            localStorage.setItem('domiciliations', JSON.stringify(domiciliations));
          } catch {
            // localStorage indisponible : le document reste généré, seul
            // l'enregistrement du suivi est perdu.
          }
        }

        // Réinitialiser les champs après sauvegarde
        setNameBanque('');
        setNumeroCompte('');
        setSalary('');
      }
      
      if (autoDownload) {
        const filename = `${template.display_name.replace(/ /g, '_')}_${employeeName.replace(/ /g, '_')}.docx`;
        triggerFileDownload(blob, filename);
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (err: any) {
      console.error('Erreur génération:', err);
      setError(err?.message || 'Impossible de générer le document. Vérifiez les données de l\'employé.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      if (!docUrl) {
        await handleGenerate(true);
        return;
      }
      const resp = await fetch(docUrl);
      const blob = await resp.blob();
      const filename = `${template.display_name.replace(/ /g, '_')}_${employeeName.replace(/ /g, '_')}.docx`;
      triggerFileDownload(blob, filename);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    } catch (err: any) {
      console.error('Erreur téléchargement:', err);
      setError(err?.message || 'Impossible de télécharger le document.');
    }
  };

  const handlePreview = async () => {
    try {
      setIsPreviewing(true);
      setError(null);

      // Fonction pour convertir YYYY-MM-DD en JJ/MM/AAAA
      const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
      };

      // Préparer les champs supplémentaires selon le template
      let extras: Record<string, string> | undefined = undefined;

      if (isMaternityTemplate) {
        // Validation
        if (!dateStartConges.trim()) {
          setError('La date de début de congés est requise.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          date_startconges: formatDate(dateStartConges),
          date_endconges: formatDate(dateEndConges),
          date_reprise: formatDate(dateReprise),
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isDomiciliationTemplate) {
        // Validation
        if (!nameBanque.trim() || !numeroCompte.trim() || !salary.trim()) {
          setError('Le nom de la banque, le numéro de compte et le salaire sont requis.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          name_banque: nameBanque,
          numero_compte: numeroCompte,
          salary: salary,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isRepriseServiceTemplate) {
        // Validation
        if (!dateDepartConges.trim()) {
          setError('La date de départ en congés est requise.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          date_depart_conges: formatDate(dateDepartConges),
          date_reprise: formatDate(dateReprise),
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isSoldeToutCompteTemplate) {
        // Validation
        if (!montantSolde.trim()) {
          setError('Le montant du solde est requis.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          montant_solde: montantSolde,
          montant_indemnit_fin_contrat: montantIndemniteFinContrat,
          montant_indemnit_conges_payes: montantIndemniteCongesPayes,
          montant_indemnit_gratif: montantIndemniteGratif,
          montant_retenues: montantRetenues,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isNotificationFinContratTemplate) {
        // Validation
        if (!reference.trim()) {
          setError('La référence du courrier est requise.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          reference: reference,
          date_etablissement: formatDate(dateEtablissement),
        };
      } else if (isProrogationCDDTemplate) {
        // Validation
        if (!numAvenant.trim()) {
          setError('Le numéro d\'avenant est requis.');
          setIsPreviewing(false);
          return;
        }
        if (!debutDate.trim()) {
          setError('La date de début est requise.');
          setIsPreviewing(false);
          return;
        }
        if (!finDate.trim()) {
          setError('La date de fin de contrat est requise.');
          setIsPreviewing(false);
          return;
        }

        extras = {
          num_avenant: numAvenant,
          debut_date: formatDate(debutDate),
          fin_date: formatDate(finDate),
          date_etablissement: formatDate(dateEtablissement),
        };
      }

      // Utiliser le même endpoint que handleGenerate mais avec save: false
      // relative_path conserve le sous-dossier de catégorie ; filename seul le
      // perdrait et le modèle serait introuvable.
      const cheminModele = template.relative_path || template.filename;
      const blob = await generateDocumentFromTemplate(employeeId, cheminModele, { extras });
      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl(blobUrl);
      setPreviewFileName(`Preview_${template.display_name}.docx`);
      setIsPreviewing(false);
    } catch (err: any) {
      console.error('Erreur prévisualisation:', err);
      setError(err?.message || 'Impossible de prévisualiser le document.');
      setIsPreviewing(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewFileName('');
  };

  return (
    <div className="fixed inset-0 bg-base-content/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Toast de succès */}
        {showToast && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
            <div className="alert alert-success shadow-lg animate-fade-in">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Document généré avec succès.</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-warning p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">{template.display_name}</h2>
              <p className="text-warning-content">
                <span className="inline-block bg-white/20 px-3 py-1 rounded-full text-sm">
                  {template.category}
                </span>
                <span className="ml-3">{employeeName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn btn-circle btn-sm bg-white/20 hover:bg-white/30 border-0 text-white"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6">
          {error && (
            <div className="alert alert-error mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {!docUrl && !generating && (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto mb-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-xl font-bold text-base-content mb-2">Prêt à générer</h3>
              <p className="text-base-content/70 mb-6">
                Le document sera généré avec les informations actuelles de l'employé.
              </p>

              {/* Formulaire pour congé de maternité */}
              {isMaternityTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de début de congés <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateStartConges}
                        onChange={(e) => setDateStartConges(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de fin de congés
                      </label>
                      <input
                        type="date"
                        value={dateEndConges}
                        onChange={(e) => setDateEndConges(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de reprise
                      </label>
                      <input
                        type="date"
                        value={dateReprise}
                        onChange={(e) => setDateReprise(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement de l'attestation <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Champs déclarés par le modèle (composeur de modèles) */}
              {saisieFields.length > 0 && (
                <div className="bg-info/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-info/20">
                  <h4 className="text-md font-bold text-base-content mb-1">Informations à renseigner</h4>
                  <p className="text-xs text-base-content/60 mb-3">Demandées par ce modèle</p>
                  <div className="space-y-3">
                    {saisieFields.map(champ => (
                      <div key={champ.key}>
                        <label className="block text-sm font-semibold text-base-content mb-1">
                          {champ.label}
                        </label>
                        <input
                          type={champ.type === 'date' ? 'date' : 'text'}
                          value={saisieValues[champ.key] || ''}
                          onChange={(e) =>
                            setSaisieValues(prev => ({ ...prev, [champ.key]: e.target.value }))
                          }
                          className="input input-bordered w-full bg-white"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulaire pour domiciliation de salaire */}
              {isDomiciliationTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Nom de la banque <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={nameBanque}
                        onChange={(e) => setNameBanque(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: BICICI, SGCI, ECOBANK..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Numéro de compte <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={numeroCompte}
                        onChange={(e) => setNumeroCompte(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 12345678901234"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Salaire net mensuel <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={salary}
                        onChange={(e) => setSalary(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 500 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Formulaire pour reprise de service */}
              {isRepriseServiceTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de départ en congés <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateDepartConges}
                        onChange={(e) => setDateDepartConges(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de reprise
                      </label>
                      <input
                        type="date"
                        value={dateReprise}
                        onChange={(e) => setDateReprise(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Formulaire pour solde de tout compte */}
              {isSoldeToutCompteTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Montant du solde <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={montantSolde}
                        onChange={(e) => setMontantSolde(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 1 500 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Indemnité de fin de contrat
                      </label>
                      <input
                        type="text"
                        value={montantIndemniteFinContrat}
                        onChange={(e) => setMontantIndemniteFinContrat(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 800 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Indemnité de congés payés
                      </label>
                      <input
                        type="text"
                        value={montantIndemniteCongesPayes}
                        onChange={(e) => setMontantIndemniteCongesPayes(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 400 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Gratification
                      </label>
                      <input
                        type="text"
                        value={montantIndemniteGratif}
                        onChange={(e) => setMontantIndemniteGratif(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 500 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Retenues fiscales et sociales
                      </label>
                      <input
                        type="text"
                        value={montantRetenues}
                        onChange={(e) => setMontantRetenues(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 200 000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Formulaire pour notification de fin de contrat */}
              {isNotificationFinContratTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Référence du courrier <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: N°123/FC/2025"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Formulaire pour prorogation CDD */}
              {isProrogationCDDTemplate && (
                <div className="bg-warning/10 rounded-lg p-4 mb-6 max-w-md mx-auto text-left border border-warning/20">
                  <h4 className="text-md font-bold text-base-content mb-4">Informations supplémentaires requises</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Numéro d'avenant <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={numAvenant}
                        onChange={(e) => setNumAvenant(e.target.value)}
                        className="input input-bordered w-full bg-white"
                        placeholder="Ex: 1, 2, 3..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date de début <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={debutDate}
                        onChange={(e) => setDebutDate(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Nouvelle date de fin de contrat (après prorogation) <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={finDate}
                        onChange={(e) => setFinDate(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-base-content mb-1">
                        Date d'établissement <span className="text-error">*</span>
                      </label>
                      <input
                        type="date"
                        value={dateEtablissement}
                        onChange={(e) => setDateEtablissement(e.target.value)}
                        className="input input-bordered w-full bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-center">
                <button
                  onClick={handlePreview}
                  className="btn btn-outline btn-lg gap-2"
                  disabled={generating || isPreviewing}
                >
                  {isPreviewing ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Prévisualiser
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleGenerate(true)}
                  className="btn btn-warning btn-lg gap-2 text-white"
                  disabled={generating || isPreviewing}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Générer le document
                </button>
              </div>
            </div>
          )}

          {generating && (
            <div className="text-center py-12">
              <span className="loading loading-spinner loading-lg text-warning mb-4"></span>
              <p className="text-base-content/70">Génération du document en cours...</p>
            </div>
          )}

          {docUrl && (
            <div className="bg-warning/10 rounded-lg p-4 border border-warning/20">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-base-content">Document généré avec succès</span>
                <span className="badge badge-success">✓ Prêt</span>
              </div>
              <div className="bg-white rounded-lg p-8 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto mb-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-base-content mb-6">Le document a été généré et est prêt à être téléchargé ou imprimé.</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleDownload}
                    className="btn btn-warning gap-2 text-white"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger
                  </button>
                  <button
                    onClick={() => window.open(docUrl, '_blank')}
                    className="btn btn-outline gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Ouvrir
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-base-300 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-ghost"
          >
            Fermer
          </button>
          {docUrl && (
            <button
              onClick={() => handleGenerate(false)}
              className="btn btn-warning gap-2 text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Régénérer
            </button>
          )}
        </div>
      </div>

      {/* DocumentViewer pour la prévisualisation */}
      {previewUrl && (
        <DocumentViewer
          fileUrl={previewUrl}
          fileName={previewFileName}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

export default DocumentGeneratorModal;